export type ReportLevel = 'basic' | 'advanced' | 'complete';

/** Mirrors `REPORT_LEVEL_RANK` in `src/types/billing.ts` — compare report
 * levels by rank, never by equality, since 'complete' must also satisfy an
 * 'advanced' requirement. */
export const REPORT_LEVEL_RANK: Record<ReportLevel, number> = {
  basic: 0,
  advanced: 1,
  complete: 2,
};

/** Mirrors `meetsLevel` in `src/types/billing.ts`: a missing rank is `basic`,
 * and a higher rung satisfies a lower requirement. */
export const meetsReportLevel = (level: ReportLevel | undefined, required: ReportLevel): boolean =>
  REPORT_LEVEL_RANK[level ?? 'basic'] >= REPORT_LEVEL_RANK[required];

/**
 * "Keep this pricing for my store only" — mirrors
 * `assertStorePrivateSheetAllowed` in `src/utils/pricingScopeGate.ts`: with
 * the `variable_pricing_ladder` flag on it needs Advanced
 * `variablePricingLevel` (Complete satisfies it; the Advanced add-on grants no
 * `customPricing`), with the flag off the `customPricing` boolean. The server
 * gate is authoritative; this only decides whether to show the upsell.
 */
export const canKeepStorePrivateSheet = (
  limits: Pick<PlanLimits, 'customPricing' | 'variablePricingLevel'>,
  ladderEnabled: boolean,
): boolean =>
  ladderEnabled ? meetsReportLevel(limits.variablePricingLevel, 'advanced') : limits.customPricing === true;

export interface PlanLimits {
  maxUsers: number;
  maxApiCalls: number;
  maxDataSync: number;
  /** Opt-in product-sync cap; maxDataSync only applies when true */
  productSyncCap?: boolean;
  /** Distinct brands the sync selection may span under productSyncCap; -1 or missing = no limit */
  maxSyncBrands?: number;
  maxIntegrations: number;
  realTimeSync: boolean;
  mapViolationAlerts: boolean;
  advancedAnalytics: boolean;
  prioritySupport: boolean;
  customIntegrations: boolean;
  whiteLabel: boolean;
  inventorySync?: boolean;
  rebatePricing?: boolean;
  rebateTracking?: boolean;
  automatedRebates?: boolean;
  customPricing?: boolean;
  customApi?: boolean;
  /** AI classification of products into the store's own taxonomy (flag taxonomy_classifier_assist) */
  aiClassification?: boolean;
  /** Any Motzu field onto the store's own Shopify metafields (flag shopify_metafield_mapping, MOT-144). */
  customMetafieldMapping?: boolean;
  /** Tabbed dealer product view in the store product browser (flag dealer_product_view); Pro and above. */
  dealerProductView?: boolean;
  /** Spec mapping crosswalk export and bulk import (flag spec_mapping_crosswalk, MOT-641); Pro and above. */
  specMappingCrosswalk?: boolean;
  /** Per-store AI title and description rewrites (flag store_ai_copy, MOT-153); the bulk tools of row B33 share it. */
  aiContentTools?: boolean;
  /** Generations per UTC month under aiContentTools; -1 = no cap, 0 or missing = none. */
  aiCopyMonthlyGenerations?: number;
  dedicatedSupport?: boolean;
  /** Model calls a store may spend on bulk AI operations per month (flag bulk_ai_operations); 0 none, -1 no cap */
  aiBulkMonthlyOperations?: number;
  /** Automated rebate claim filing; dark behind the rebate_claim_automation flag */
  rebateClaimAutomation?: boolean;
  /**
   * Intelligence grandfather for cross-integration (integration-scoped)
   * pricing under the variable-pricing ladder; no tier sets it, an admin
   * grants it. Mirrors `src/types/billing.ts`.
   */
  crossIntegrationPricingLegacy?: boolean;
  reportLevel?: ReportLevel;
  /** Variable pricing rung (flag variable_pricing_ladder): location/zone scope at advanced, integration scope at complete */
  variablePricingLevel?: ReportLevel;
  /** Manufacturer reporting rung (flag manufacturer_reporting); its own key, never reportLevel */
  manufacturerReportLevel?: ReportLevel;
  /** MAP auditing rung (flag map_audit_ladder); its own key, never reportLevel */
  mapAuditLevel?: ReportLevel;
  /** Mapped products per MAP audit run (flag map_audit_ladder); -1 = no cap, missing = 100 */
  maxMapAuditProducts?: number;
}

/**
 * The ranked limits, shown as a Basic / Advanced / Complete picker in the
 * Admin → Plans editor. Mirrors `RankedLimitKey` in `src/types/billing.ts`.
 * Never in `GRANTABLE_FEATURES`: a grant that could move a rank could move
 * it down.
 */
export type RankedLimitKey = 'reportLevel' | 'variablePricingLevel' | 'manufacturerReportLevel' | 'mapAuditLevel';

export const RANKED_LIMITS: Array<{ key: RankedLimitKey; label: string; helper: string }> = [
  { key: 'reportLevel', label: 'Reporting level', helper: 'Advanced opens Top Selling Products and the CSV export.' },
  {
    key: 'variablePricingLevel',
    label: 'Variable pricing level',
    helper: 'Advanced prices one integration\u2019s own locations and zones; Complete prices across integrations.',
  },
  {
    key: 'manufacturerReportLevel',
    label: 'Manufacturer reporting level',
    helper: 'Advanced opens the Sales Report by manufacturer.',
  },
  {
    key: 'mapAuditLevel',
    label: 'MAP auditing level',
    helper: 'Every tier audits Shopify listings at Basic. Advanced and Complete are held for the cross-integration audit.',
  },
];

/**
 * Boolean plan features an admin can toggle on a plan or hand to one store as
 * a grandfather grant. Numeric caps and the ranks (`RANKED_LIMITS`) are
 * deliberately absent: a grant only ever flips a boolean on, never moves a
 * cap or a rank.
 * `productSyncCap` is excluded because it turns a cap ON — "granting" it would
 * take something away.
 */
export const GRANTABLE_FEATURES: Array<{ key: keyof PlanLimits; label: string }> = [
  { key: 'inventorySync', label: 'Inventory syncing' },
  { key: 'rebatePricing', label: 'Rebate pricing' },
  { key: 'rebateTracking', label: 'Rebate tracking' },
  { key: 'automatedRebates', label: 'Automated rebates' },
  { key: 'rebateClaimAutomation', label: 'Automated rebate claims' },
  { key: 'customPricing', label: 'Custom pricing (legacy switch)' },
  { key: 'crossIntegrationPricingLegacy', label: 'Cross-integration pricing (grandfathered Intelligence)' },
  { key: 'mapViolationAlerts', label: 'MAP violation alerts' },
  { key: 'customApi', label: 'Custom APIs' },
  { key: 'aiClassification', label: 'AI classification' },
  { key: 'customMetafieldMapping', label: 'Custom metafield mapping (Shopify)' },
  { key: 'dealerProductView', label: 'Dealer product view (product browser)' },
  { key: 'specMappingCrosswalk', label: 'Spec mapping crosswalk (export and bulk import)' },
  { key: 'dedicatedSupport', label: 'Dedicated support' },
  { key: 'realTimeSync', label: 'Real-time sync' },
  { key: 'advancedAnalytics', label: 'Advanced analytics' },
  { key: 'prioritySupport', label: 'Priority support' },
  { key: 'customIntegrations', label: 'Custom integrations' },
  { key: 'whiteLabel', label: 'White-label' },
];

/**
 * The switches the Admin → Plans editor shows: `GRANTABLE_FEATURES` minus the
 * ones that only exist as a per-organization grant. The cross-integration
 * grandfather on a plan would reach every subscriber, new stores included, so
 * the server refuses it there too.
 */
export const PLAN_PACKAGING_FEATURES: Array<{ key: keyof PlanLimits; label: string }> = [
  ...GRANTABLE_FEATURES.filter(f => f.key !== 'crossIntegrationPricingLegacy'),
  // Sold by tier only, never granted: the boolean is useless without the
  // monthly caps `aiCopyMonthlyGenerations` and `aiBulkMonthlyOperations`,
  // which a grant never raises (`NON_GRANTABLE_FEATURE_KEYS` on the server).
  { key: 'aiContentTools', label: 'AI content tools (store copy rewrites and bulk AI)' },
];

export interface RegionalPrice {
  price: number;
  currency: string;
  stripePriceId?: string;
}

export interface BillingPlan {
  id: string;
  name: string;
  description: string;
  whoItsFor: string;
  price: number;
  currency: string;
  interval: 'monthly' | 'yearly' | 'lifetime';
  setupFee?: number;
  /** Quoted per dealer instead of priced (Complete); never with a numeric fee */
  setupFeeCustom?: boolean;
  /** Stripe one-time Price collected at Checkout for the setup fee */
  setupFeeStripePriceId?: string;
  features: string[];
  limits?: PlanLimits;
  maxUsers?: number;
  maxApiCalls?: number;
  maxDataSync?: number;
  isActive: boolean;
  isPopular?: boolean;
  stripePriceId?: string;
  regionalPrices?: Record<string, RegionalPrice>;
  createdAt: string;
  updatedAt: string;
}

export interface SetupFeeWaiver {
  campaign?: string | null;
  reason: string;
  grantedBy?: string | null;
  grantedAt: string;
  expiresAt: string | null;
}

export interface SetupFeeWaiverCampaign {
  key: string;
  label: string;
  eventEndsOn: string;
  windowDays: number;
  expiresAt: string;
  open: boolean;
}

export interface UserSubscription {
  id: string;
  userId: string;
  organizationId?: string;
  planId: string;
  plan?: BillingPlan;
  status: 'active' | 'canceled' | 'past_due' | 'unpaid' | 'trialing';
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  trialEnd?: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingUsage {
  userId: string;
  planId: string;
  apiCallsUsed: number;
  dataSyncUsed: number;
  periodStart: string;
  periodEnd: string;
  lastUpdated: string;
}

export interface BillingInvoice {
  id: string;
  userId?: string;
  organizationId?: string;
  customerId?: string;
  subscriptionId?: string;
  amount: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible' | 'draft';
  description?: string;
  invoiceDate?: string;
  dueDate?: string;
  paidAt?: string;
  invoiceUrl?: string;
  pdfUrl?: string;
  createdAt: string;
}

/**
 * `GET /billing/admin/stats`. Counts are stores (one subscription per store,
 * the one that governs it) under the shared definition in the API's
 * `services/adminMetrics.ts`, so Billing, Plans and the Dashboard agree.
 */
export interface BillingStats {
  totalRevenue: number;
  monthlyRevenue: number;
  /** Stores on a live subscription to a priced plan (paying, comp or trial). */
  activeSubscriptions: number;
  /** Stores counted in MRR. */
  payingSubscriptions?: number;
  trialSubscriptions: number;
  canceledSubscriptions: number;
  churnRate: number;
  averageRevenuePerUser: number;
  popularPlan: string;
  storeCount?: number;
  storesWithoutPaidPlan?: number;
  revenueByPlan: Array<{
    planId?: string;
    planName: string;
    revenue: number;
    subscribers: number;
  }>;
}

export interface PaymentMethod {
  id: string;
  userId: string;
  type: 'card' | 'bank_account' | 'paypal';
  last4?: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
  createdAt: string;
}

export interface BillingWebhook {
  id: string;
  type: string;
  data: any;
  processed: boolean;
  createdAt: string;
}

export interface CreateSubscriptionRequest {
  planId: string;
  paymentMethodId?: string;
  couponCode?: string;
}

export interface UpdateSubscriptionRequest {
  planId?: string;
  cancelAtPeriodEnd?: boolean;
}

export interface BillingAnalytics {
  revenueGrowth: number;
  subscriberGrowth: number;
  churnRate: number;
  monthlyRecurringRevenue: number;
  customerLifetimeValue: number;
  conversionRate: number;
}
